/**
 * Revenue Overview
 * Single-pane snapshot of the revenue side: income streams, collection
 * health, recurring engine status, and recent activity. Lives as the first
 * tab under Revenue.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp,
  TrendingDown,
  FileText,
  AlertCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  HandCoins,
  CreditCard,
  CheckCircle2,
  Plus,
  Building2,
  CalendarDays,
  Calendar,
  CalendarClock,
} from 'lucide-react';

interface InvoiceDashboardSummary {
  totalRevenue: number;
  outstandingAmount: number;
  overdueAmount: number;
  overdueCount: number;
  draftCount: number;
  totalInvoices: number;
  paidCount: number;
  monthlyRevenue: number;
  ytdRevenue: number;
  lastMonthRevenue: number;
}

interface RecentInvoice {
  id: string;
  invoiceNumber: number;
  accountName: string;
  businessProfileName: string;
  status: string;
  total: number;
  amountPaid: number;
  dueDate: string;
  createdAt: string;
}

interface RecurringSchedule {
  id: string;
  accountName: string;
  businessProfileName: string;
  name: string;
  frequency: string;
  billingDayOfMonth: number;
  nextBillingDate: string;
  status: string;
  subtotal: number;
  enablePaymentReminders: boolean;
}

interface Payment {
  id: string;
  invoiceId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber: string | null;
  invoiceNumber: number | null;
  accountName: string | null;
  businessProfileName: string | null;
}

interface RevenueByProfileRow {
  businessProfileId: string;
  businessProfileName: string;
  fiscalYear: number;
  fiscalYearEndMonth: number;
  fiscalYearStart: string;
  fiscalYearEnd: string;
  invoiceCount: number;
  unpaidCount: number;
  paidCount: number;
  draftCount: number;
  overdueCount: number;
  overdueAmount: number;
  otherIncomeCount: number;
  invoiceRevenue: number;
  otherIncomeRevenue: number;
  totalRevenue: number;
  fytdRevenue: number;
  ytdRevenue: number;
  monthlyRevenue: number;
  lastMonthRevenue: number;
  outstandingAmount: number;
}

interface MonthlyRevenueTrendRow {
  monthStart: string;
  monthEnd: string;
  year: number;
  month: number;
  invoiceRevenue: number;
  otherIncomeRevenue: number;
  totalRevenue: number;
}

interface OtherIncomeRow {
  id: string;
  description: string;
  amount: number;
  incomeDate: string;
  incomeType: string;
  businessProfileName?: string | null;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'destructive',
  cancelled: 'secondary',
  void: 'secondary',
};

const methodLabels: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire',
  credit_card: 'Credit Card',
  cash: 'Cash',
  other: 'Other',
};

const frequencyToMonthlyFactor: Record<string, number> = {
  monthly: 1,
  quarterly: 1 / 3,
  semi_annual: 1 / 6,
  annual: 1 / 12,
};

function getMonthStart(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
}

export function RevenueOverview() {
  const navigate = useNavigate();

  const { data: invoiceData, isLoading } = useQuery({
    queryKey: ['finance-dashboard-summary'],
    queryFn: async () => {
      const result = await api.getInvoiceSummary();
      return result.data as { summary: InvoiceDashboardSummary; recentInvoices: RecentInvoice[] };
    },
  });

  const { data: recurringData } = useQuery({
    queryKey: ['recurringSchedules', { overview: true }],
    queryFn: () => api.getRecurringSchedules({ limit: 100 }),
  });

  const { data: paymentsData } = useQuery({
    queryKey: ['recentPayments'],
    queryFn: () => api.getPayments({ page: 1, limit: 5 }),
  });

  const { data: otherIncomeData } = useQuery({
    queryKey: ['otherIncomeRecent'],
    queryFn: () => api.getOtherIncome({ page: 1, limit: 100 }),
  });

  const { data: revenueByProfileData } = useQuery({
    queryKey: ['revenueByProfile'],
    queryFn: () => api.getRevenueByProfile(),
  });

  const { data: monthlyTrendData } = useQuery({
    queryKey: ['monthlyRevenueTrend', { months: 6 }],
    queryFn: () => api.getMonthlyRevenueTrend(6),
  });

  const summary = invoiceData?.summary;
  const recentInvoices = invoiceData?.recentInvoices || [];
  const schedules = (recurringData?.data || []) as RecurringSchedule[];
  const payments = (paymentsData?.data || []) as Payment[];
  const otherIncomeRows = (otherIncomeData?.data || []) as OtherIncomeRow[];
  const revenueByProfile = (revenueByProfileData?.data || []) as RevenueByProfileRow[];
  const monthlyTrend = (monthlyTrendData?.data || []) as MonthlyRevenueTrendRow[];

  // Derived metrics
  const monthlyRevenue = summary?.monthlyRevenue || 0;
  const totalRevenue = summary?.totalRevenue || 0;
  const ytdRevenue = summary?.ytdRevenue || 0;
  const lastMonthRevenue = summary?.lastMonthRevenue || 0;
  const monthOverMonthDelta = monthlyRevenue - lastMonthRevenue;
  const monthOverMonthPct = lastMonthRevenue > 0 ? (monthOverMonthDelta / lastMonthRevenue) * 100 : null;
  const outstandingAmount = summary?.outstandingAmount || 0;
  const overdueAmount = summary?.overdueAmount || 0;
  const overdueCount = summary?.overdueCount || 0;
  const totalInvoices = summary?.totalInvoices || 0;
  const paidCount = summary?.paidCount || 0;
  const draftCount = summary?.draftCount || 0;
  const unpaidCount = totalInvoices - paidCount;
  const collectionRate = totalInvoices > 0 ? (paidCount / totalInvoices) * 100 : 0;
  const avgInvoiceSize = totalInvoices > 0 ? totalRevenue / totalInvoices : 0;

  // MRR = sum of active schedules normalized to monthly
  const activeSchedules = schedules.filter(s => s.status === 'active');
  const monthlyRecurringRevenue = activeSchedules.reduce(
    (sum, s) => sum + s.subtotal * (frequencyToMonthlyFactor[s.frequency] || 1),
    0
  );

  // Next schedule to run
  const nextSchedule = activeSchedules
    .slice()
    .sort((a, b) => new Date(a.nextBillingDate).getTime() - new Date(b.nextBillingDate).getTime())[0];

  // Other income totals (MTD)
  const monthStart = getMonthStart();
  const otherIncomeMtd = otherIncomeRows
    .filter(r => r.incomeDate >= monthStart)
    .reduce((sum, r) => sum + r.amount, 0);
  const otherIncomeYtd = otherIncomeRows.reduce((sum, r) => sum + r.amount, 0);

  // Income mix (monthly)
  const totalMonthlyIncome = monthlyRevenue + otherIncomeMtd;
  const invoiceSharePct = totalMonthlyIncome > 0 ? (monthlyRevenue / totalMonthlyIncome) * 100 : 0;
  const otherSharePct = totalMonthlyIncome > 0 ? (otherIncomeMtd / totalMonthlyIncome) * 100 : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period stat cards: YTD / MTD / Last Month */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10"><CalendarDays className="h-5 w-5 text-emerald-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Revenue YTD</p>
                <p className="text-2xl font-bold">{formatCurrency(ytdRevenue)}</p>
                <p className="text-xs text-muted-foreground">Lifetime: {formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><Calendar className="h-5 w-5 text-green-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Revenue MTD</p>
                <p className="text-2xl font-bold">{formatCurrency(monthlyRevenue)}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  vs last month
                  {monthOverMonthPct !== null ? (
                    <span className={`inline-flex items-center gap-0.5 ${monthOverMonthDelta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {monthOverMonthDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {monthOverMonthDelta >= 0 ? '+' : ''}{monthOverMonthPct.toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-500/10"><CalendarClock className="h-5 w-5 text-slate-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Last Month Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(lastMonthRevenue)}</p>
                <p className="text-xs text-muted-foreground">Prior calendar month</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operational stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><RefreshCw className="h-5 w-5 text-blue-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Monthly Recurring Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(monthlyRecurringRevenue)}</p>
                <p className="text-xs text-muted-foreground">
                  {activeSchedules.length} active schedule{activeSchedules.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><Clock className="h-5 w-5 text-amber-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Outstanding A/R</p>
                <p className="text-2xl font-bold">{formatCurrency(outstandingAmount)}</p>
                <p className="text-xs text-muted-foreground">{unpaidCount} unpaid invoice{unpaidCount !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={overdueCount > 0 ? 'border-red-500/30' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${overdueCount > 0 ? 'bg-red-500/10' : 'bg-muted'}`}>
                <AlertCircle className={`h-5 w-5 ${overdueCount > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : ''}`}>
                  {formatCurrency(overdueAmount)}
                </p>
                <p className="text-xs text-muted-foreground">{overdueCount} invoice{overdueCount !== 1 ? 's' : ''} past due</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 6-month revenue trend */}
      {monthlyTrend.length > 0 && (() => {
        const maxMonthRevenue = Math.max(...monthlyTrend.map(m => m.totalRevenue), 1);
        const trendTotal = monthlyTrend.reduce((s, m) => s + m.totalRevenue, 0);
        const trendAvg = monthlyTrend.length > 0 ? trendTotal / monthlyTrend.length : 0;
        const monthLabel = (m: MonthlyRevenueTrendRow) =>
          new Date(m.year, m.month - 1, 1).toLocaleString('en-US', { month: 'short' });
        const yearLabel = (m: MonthlyRevenueTrendRow) =>
          new Date(m.year, m.month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
        const first = monthlyTrend[0];
        const last = monthlyTrend[monthlyTrend.length - 1];
        const periodDelta = last.totalRevenue - first.totalRevenue;
        const periodPct = first.totalRevenue > 0 ? (periodDelta / first.totalRevenue) * 100 : null;
        return (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Monthly Revenue — Last {monthlyTrend.length} Months
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {yearLabel(first)} → {yearLabel(last)}
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Avg / month</div>
                <div className="text-lg font-bold">{formatCurrency(trendAvg)}</div>
                {periodPct !== null && (
                  <div className={`text-xs flex items-center justify-end gap-0.5 ${periodDelta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {periodDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {periodDelta >= 0 ? '+' : ''}{periodPct.toFixed(0)}% vs {monthLabel(first)}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-3 h-40">
                {monthlyTrend.map((m, i) => {
                  const heightPct = (m.totalRevenue / maxMonthRevenue) * 100;
                  const isCurrent = i === monthlyTrend.length - 1;
                  return (
                    <div key={`${m.year}-${m.month}`} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="text-xs font-medium truncate w-full text-center">
                        {formatCurrency(m.totalRevenue)}
                      </div>
                      <div className="w-full flex-1 flex items-end">
                        <div
                          className={`w-full rounded-t transition-all duration-500 ${isCurrent ? 'bg-emerald-500' : 'bg-emerald-500/60'}`}
                          style={{ height: `${Math.max(heightPct, 2)}%` }}
                          title={`${yearLabel(m)}: ${formatCurrency(m.totalRevenue)}`}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">{monthLabel(m)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                <span>Period total: <span className="font-semibold text-foreground">{formatCurrency(trendTotal)}</span></span>
                <span>
                  High: {formatCurrency(maxMonthRevenue)} · Low: {formatCurrency(Math.min(...monthlyTrend.map(m => m.totalRevenue)))}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Two-column main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Revenue by Business Profile */}
          {revenueByProfile.length > 0 && (() => {
            const maxRevenue = Math.max(...revenueByProfile.map(p => p.totalRevenue), 1);
            const totalAcrossProfiles = revenueByProfile.reduce((s, p) => s + p.totalRevenue, 0);
            const totalInvoiceAcross = revenueByProfile.reduce((s, p) => s + p.invoiceRevenue, 0);
            const totalOtherAcross = revenueByProfile.reduce((s, p) => s + p.otherIncomeRevenue, 0);
            // Palette — cycles if > 5 profiles. Keeps color stable by index.
            const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-pink-500', 'bg-cyan-500'];
            return (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Revenue by Business Profile
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/billing/business-profiles')}>
                    Manage <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {revenueByProfile.map((p, i) => {
                    const widthPct = (p.totalRevenue / maxRevenue) * 100;
                    const invoiceWidthPct = p.totalRevenue > 0 ? (p.invoiceRevenue / p.totalRevenue) * widthPct : 0;
                    const sharePct = totalAcrossProfiles > 0 ? (p.totalRevenue / totalAcrossProfiles) * 100 : 0;
                    const color = colors[i % colors.length];
                    const recordParts: string[] = [];
                    if (p.invoiceCount > 0) recordParts.push(`${p.invoiceCount} invoice${p.invoiceCount !== 1 ? 's' : ''}`);
                    if (p.otherIncomeCount > 0) recordParts.push(`${p.otherIncomeCount} other-income`);
                    const recordSummary = recordParts.length > 0 ? recordParts.join(' · ') : 'No revenue recorded';
                    return (
                      <div key={p.businessProfileId}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium truncate block">{p.businessProfileName}</span>
                            <span className="text-xs text-muted-foreground">
                              {recordSummary}
                              {p.outstandingAmount > 0 && (
                                <span className="ml-2 text-amber-600">
                                  · {formatCurrency(p.outstandingAmount)} outstanding
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="text-right ml-3">
                            <div className="font-semibold">{formatCurrency(p.totalRevenue)}</div>
                            <div
                              className="text-xs text-muted-foreground"
                              title={`FY ${p.fiscalYear}: ${p.fiscalYearStart} → ${p.fiscalYearEnd}`}
                            >
                              {sharePct.toFixed(0)}% · FYTD {p.fiscalYear} {formatCurrency(p.fytdRevenue)}
                            </div>
                            {p.fiscalYearEndMonth !== 12 && (
                              <div className="text-xs text-muted-foreground" title="Calendar year to date">
                                YTD {formatCurrency(p.ytdRevenue)}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              MTD {formatCurrency(p.monthlyRevenue)} · Last mo {formatCurrency(p.lastMonthRevenue)}
                            </div>
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                          <div className={`h-full ${color} opacity-80 transition-all duration-500`} style={{ width: `${invoiceWidthPct}%` }} />
                          <div className={`h-full ${color} opacity-40 transition-all duration-500`} style={{ width: `${widthPct - invoiceWidthPct}%` }} />
                        </div>
                        {(p.invoiceRevenue > 0 || p.otherIncomeRevenue > 0) && (
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              Invoices {formatCurrency(p.invoiceRevenue)}
                            </span>
                            <span className="flex items-center gap-1">
                              <HandCoins className="h-3 w-3" />
                              Other {formatCurrency(p.otherIncomeRevenue)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="pt-3 border-t space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {revenueByProfile.length} profile{revenueByProfile.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm font-bold">Total: {formatCurrency(totalAcrossProfiles)}</span>
                    </div>
                    {totalAcrossProfiles > 0 && (
                      <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          Invoices {formatCurrency(totalInvoiceAcross)}
                        </span>
                        <span className="flex items-center gap-1">
                          <HandCoins className="h-3 w-3" />
                          Other {formatCurrency(totalOtherAcross)}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Revenue Mix */}
          {totalMonthlyIncome > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Revenue Mix (This Month)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span>Invoice Revenue</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold">{formatCurrency(monthlyRevenue)}</span>
                      <span className="text-xs text-muted-foreground ml-2">{invoiceSharePct.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500/60" style={{ width: `${invoiceSharePct}%` }} />
                  </div>
                </div>

                {otherIncomeMtd > 0 && (
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <HandCoins className="h-4 w-4 text-purple-500" />
                        <span>Other Income</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold">{formatCurrency(otherIncomeMtd)}</span>
                        <span className="text-xs text-muted-foreground ml-2">{otherSharePct.toFixed(0)}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500/60" style={{ width: `${otherSharePct}%` }} />
                    </div>
                  </div>
                )}

                <div className="pt-3 border-t flex items-center justify-between">
                  <span className="text-sm font-medium">Total Income</span>
                  <span className="text-lg font-bold">{formatCurrency(totalMonthlyIncome)}</span>
                </div>

                {otherIncomeYtd > 0 && (
                  <p className="text-xs text-muted-foreground">
                    YTD Other Income: {formatCurrency(otherIncomeYtd)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Collection Health */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Collection Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Collection Rate</p>
                  <p className="text-xl font-bold">{collectionRate.toFixed(0)}%</p>
                  <p className="text-xs text-muted-foreground">{paidCount} of {totalInvoices} paid</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Avg Invoice</p>
                  <p className="text-xl font-bold">{formatCurrency(avgInvoiceSize)}</p>
                  <p className="text-xs text-muted-foreground">Lifetime average</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Unpaid</p>
                  <p className="text-xl font-bold">{unpaidCount}</p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(outstandingAmount)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Drafts</p>
                  <p className="text-xl font-bold">{draftCount}</p>
                  <p className="text-xs text-muted-foreground">Not yet sent</p>
                </div>
              </div>

              {overdueCount > 0 && (
                <div className="mt-4 p-3 rounded-md bg-red-500/5 border border-red-500/20 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-red-700 dark:text-red-400 font-medium">
                      {overdueCount} overdue · {formatCurrency(overdueAmount)} to collect
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/billing/invoices?status=overdue')}
                  >
                    View <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Invoices */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Recent Invoices</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/invoices')}>
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {recentInvoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No invoices yet</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/billing/invoices/new')}>
                    <Plus className="h-3 w-3 mr-1" />
                    Create your first invoice
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentInvoices.slice(0, 5).map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-2.5 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="font-mono text-xs text-muted-foreground">#{invoice.invoiceNumber}</div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{invoice.accountName}</div>
                          <div className="text-xs text-muted-foreground truncate">{invoice.businessProfileName}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="font-medium text-sm">{formatCurrency(invoice.total)}</div>
                          <div className="text-xs text-muted-foreground">Due {formatDate(invoice.dueDate)}</div>
                        </div>
                        <Badge variant={statusVariant[invoice.status] || 'secondary'} className="text-xs">
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN (1/3) */}
        <div className="space-y-6">
          {/* Recurring Engine */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Recurring Engine
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/recurring')}>
                <ArrowRight className="h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeSchedules.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <p className="text-sm">No active schedules</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/billing/recurring/new')}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add schedule
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Active Schedules</span>
                    <span className="font-semibold">{activeSchedules.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">MRR</span>
                    <span className="font-semibold">{formatCurrency(monthlyRecurringRevenue)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Projected Annual</span>
                    <span className="font-semibold">{formatCurrency(monthlyRecurringRevenue * 12)}</span>
                  </div>
                  {nextSchedule && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-muted-foreground mb-1">Next to run</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{nextSchedule.accountName}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                          {formatDate(nextSchedule.nextBillingDate)}
                        </span>
                      </div>
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="w-full" onClick={() => navigate('/billing/generate')}>
                    Generate Invoices
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Recent Payments
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/revenue')}>
                <ArrowRight className="h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No payments yet</p>
              ) : (
                <div className="space-y-2">
                  {payments.slice(0, 5).map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {p.accountName || `#${p.invoiceNumber || '?'}`}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(p.paymentDate)} · {methodLabels[p.paymentMethod] || p.paymentMethod}
                        </div>
                      </div>
                      <span className="font-semibold text-green-600 ml-2 whitespace-nowrap">
                        {formatCurrency(p.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/invoices/new')}>
                <FileText className="h-4 w-4 mr-2" />
                New Invoice
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/recurring/new')}>
                <RefreshCw className="h-4 w-4 mr-2" />
                New Recurring Schedule
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/other-income')}>
                <HandCoins className="h-4 w-4 mr-2" />
                Record Other Income
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/revenue')}>
                <CreditCard className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
